/** @jsx jsx */
import React, {
	useState,
	useMemo,
	useCallback,
	useRef,
	type FocusEvent,
	useEffect,
	forwardRef,
	useLayoutEffect,
} from 'react';
import { cssMap, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { v4 as uuid } from 'uuid';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation } from 'react-relay';
import {
	components,
	type StylesConfig,
	type MultiValueGenericProps,
	type MultiValueRemoveProps,
	type OptionProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import { useIntl } from '@atlassian/jira-intl';
import type { SessionId } from '@atlassian/jira-issue-analytics/src/common/types.tsx';
import { FailedFetchOptionWrapper } from '@atlassian/jira-issue-field-select-base/src/ui/failed-fetch-options/index.tsx';
import { filterOptionByLabelAndFilterValues } from '@atlassian/jira-issue-field-select-base/src/ui/filter-options-by-label-and-filter-values/index.tsx';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import type { LabelOption as Option } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import { LABELS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { LabelsSuggestionList } from '@atlassian/jira-shared-types/src/rest/jira/label.tsx';
import { LabelsColorPickerForm } from '@atlassian/jira-option-color-picker/src/labels-color-picker/index.tsx';
import type { ColorOptionData } from '@atlassian/jira-option-color-picker/src/labels-color-picker/types.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type {
	edit_labelsUpdateColorMutation,
	JiraOptionColorInput,
} from '@atlassian/jira-relay/src/__generated__/edit_labelsUpdateColorMutation.graphql';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { GREY_META } from '@atlassian/jira-option-color-picker/src/color-picker/constants.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { mapLabelsResponseToLabelOptions } from '../../common/utils.tsx';
import { useLabelsSuggestions } from '../../services/suggestions/index.tsx';
import CreatableSelectWithAnalytics from './creatable-select-with-analytics/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const FETCH_DEBOUNCE = 300;

const multiValueStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div > div': {
			paddingLeft: token('space.0'),
			paddingRight: token('space.0'),
		},
	},
});

const multiValueLabelStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div': {
			borderColor: 'transparent',
		},
	},
});

const labelsValueStyles: StylesConfig<Option, true> = {
	multiValue: (base, { data }) => {
		// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
		const dataLabel: string = data.label;

		const { backgroundColor: backgroundColorMilestone1 } = getColorMetadata(
			memoizeGetAutoGeneratedColorForLabels(dataLabel),
		);

		const isNewLabel = data.__isNew__;
		const newLabelColor = data.color ?? GREY_META.name;
		const oldLabelColor = data.color ?? memoizeGetAutoGeneratedColorForLabels(dataLabel);
		const { backgroundColor: backgroundColorMilestone2 } = getColorMetadata(
			isNewLabel ? newLabelColor : oldLabelColor,
		);

		const backgroundColor = expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
			? backgroundColorMilestone2
			: backgroundColorMilestone1;

		return {
			...base,
			border: `1px solid ${backgroundColor}`,
			'& > div > div > div': {
				paddingLeft: 0,
				paddingRight: 0,
			},
		};
	},
	multiValueLabel: (base) => ({
		...base,
		'& > div > div': {
			borderColor: 'transparent',
		},
	}),
};

export const LabelsEdit = (props: Props): JSX.Element => {
	const {
		autoCompleteUrl,
		autoFocus,
		onChange,
		onFocus,
		onBlur,
		spacing,
		value,
		options,
		cachedOptions = [],
		formatCreateLabel,
		noOptionsMessage,
		loadingMessage,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		getSuggestions,
		fetchSuggestionsOnMount = true,
		fieldId = LABELS_TYPE,
		isInvalid = false,
		isDisabled = false,
		ariaLabel = '',
		inputId,
		isRequired,
		openMenuOnFocus,
	} = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [suggestions, setSuggestions] = useState<Option[]>();
	const [defaultSuggestions, setDefaultSuggestions] = useState<Option[]>();
	const [hasLastFetchFailed, setHasLastFetchFailed] = useState<boolean>(false);
	const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
	const lastQuery = useRef<string>('');
	const sessionId = useRef<SessionId>(uuid());

	const { data: project } = useProjectContext();

	const cloudId = useCloudId();

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<edit_labelsUpdateColorMutation>(graphql`
		mutation edit_labelsUpdateColorMutation($input: JiraBulkLabelColorUpdateInput!)
		@raw_response_type {
			jira {
				bulkUpdateLabelColor(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const isProjectAdmin = project?.isProjectAdmin || false;
	const isGlobalAdmin = useIsAdmin();

	const { formatMessage } = useIntl();
	const [{ isLoadingSuggestions }, { getLabelsSuggestions, setIsLoadingSuggestions }] =
		useLabelsSuggestions({ fieldId, getSuggestions });

	const getFailedFetchMessage = useCallback(
		() => (
			<FailedFetchOptionWrapper>{formatMessage(messages.failedFetch)}</FailedFetchOptionWrapper>
		),
		[formatMessage],
	);

	const fetchSuggestions = useCallback(
		async (query = ''): Promise<void> => {
			try {
				const response: LabelsSuggestionList = await getLabelsSuggestions(
					autoCompleteUrl,
					query,
					sessionId.current,
				);

				setIsLoadingSuggestions(false);

				if (lastQuery.current === query) {
					const results = mapLabelsResponseToLabelOptions(response);

					setSuggestions(results);

					if (query === '') {
						setDefaultSuggestions(results);
					}
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setIsLoadingSuggestions(false);
				setHasLastFetchFailed(true);
				setSuggestions([
					{
						label: getFailedFetchMessage(),
						value: '',
						isDisabled: true,
					},
				]);
			}
		},
		[
			autoCompleteUrl,
			getFailedFetchMessage,
			getLabelsSuggestions,
			lastQuery,
			sessionId,
			setHasLastFetchFailed,
			setIsLoadingSuggestions,
		],
	);

	useEffect(() => {
		if (fetchSuggestionsOnMount) {
			if (!options && !defaultSuggestions) {
				setIsLoadingSuggestions(true);
				fetchSuggestions();
			} else if (options && !defaultSuggestions) {
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state, react-you-might-not-need-an-effect/no-derived-state
				setDefaultSuggestions(options);
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state, react-you-might-not-need-an-effect/no-derived-state
				setSuggestions(options);
			} else {
				lastQuery.current = '';
				setIsLoadingSuggestions(true);
				fetchSuggestions();
			}
		}
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const groupedSuggestions = useMemo(
		() => [
			{
				label: formatMessage(messages.recent),
				options: cachedOptions,
			},
			{
				label: formatMessage(messages.all),
				options: suggestions || [],
			},
		],
		[cachedOptions, formatMessage, suggestions],
	);

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFetchSuggestions = useCallback(debounce(fetchSuggestions, FETCH_DEBOUNCE), []);

	const allLabels = useCallback(
		() => [...(cachedOptions ?? []), ...(suggestions ?? [])],
		[cachedOptions, suggestions],
	);

	const isNewLabelText = useCallback(
		(inputValue?: string) => {
			const isNewLabel = !allLabels().some((option) => {
				// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
				const optionLabel: string = option.label;

				return optionLabel === inputValue;
			});
			return isNewLabel;
		},
		[allLabels],
	);

	const getCreateLabel = useCallback(
		(inputValue: string): string => {
			return `${inputValue} (${
				formatCreateLabel !== undefined ? formatCreateLabel : formatMessage(messages.createNewItem)
			})`;
		},
		[formatCreateLabel, formatMessage],
	);

	const getNoOptionsMessage = useCallback(
		(): string =>
			noOptionsMessage !== undefined ? noOptionsMessage : formatMessage(messages.empty),
		[formatMessage, noOptionsMessage],
	);
	const getLoadingMessage = useCallback(
		(): string => (loadingMessage !== undefined ? loadingMessage : formatMessage(messages.loading)),
		[formatMessage, loadingMessage],
	);

	const filterOption = useCallback(
		(option: Option, query: string): boolean => {
			if (hasLastFetchFailed) {
				// If the fetch has failed, we will show a disabled option to indicate the error to users
				return true;
			}
			return filterOptionByLabelAndFilterValues<Option>(option, query || '');
		},
		[hasLastFetchFailed],
	);

	const isFocusingRef = useRef<boolean>(false);

	const onQueryChange = useCallback(
		(query: string): void => {
			debouncedFetchSuggestions.cancel();
			setHasLastFetchFailed(false);
			if (
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
			) {
				setMenuIsOpen(true);
				requestAnimationFrame(() => {
					if (isFocusingRef.current === false) {
						if (query) {
							lastQuery.current = query;
							setIsLoadingSuggestions(true);
							debouncedFetchSuggestions(query);
						} else {
							lastQuery.current = '';
							setIsLoadingSuggestions(false);
							setSuggestions(defaultSuggestions);
						}
					}
				});
			} else if (query) {
				lastQuery.current = query;
				setIsLoadingSuggestions(true);
				debouncedFetchSuggestions(query);
			} else {
				lastQuery.current = '';
				setIsLoadingSuggestions(false);
				setSuggestions(defaultSuggestions);
			}
		},
		[debouncedFetchSuggestions, defaultSuggestions, setIsLoadingSuggestions, setSuggestions],
	);

	const [selectedLabelForEdit, setSelectedLabelForEdit] = useState<Option>();

	const [showColorPickerPopup, setShowColorPickerPopup] = useState(false);

	const [showColorPickerPopupWhenCreated, setShowColorPickerPopupWhenCreated] = useState(false);

	const [isManuallyFocusing, setIsManuallyFocusing] = useState(false);
	const lastFocusedKey = useRef<{ key: string; index: number; type: string } | null>(null);

	const onSelectFocus = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			if (!suggestions) {
				setIsLoadingSuggestions(true);
				fetchSuggestions('');
			}
			if (
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
			) {
				setMenuIsOpen(!menuIsOpen);
				isFocusingRef.current = false;
				lastFocusedKey.current = { key: 'input', index: 0, type: 'input' };
			}
			onFocus && onFocus(e);
		},
		[suggestions, menuIsOpen, onFocus, setIsLoadingSuggestions, fetchSuggestions],
	);

	const onSelectBlur = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			if (
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
			) {
				requestAnimationFrame(() => {
					const button = globalThis.document
						.querySelector(
							'div[role="group"] > button[type="button"] [data-testid="option-color-picker.option-tag.field-tag-wrapper"]',
						)
						?.closest('button');

					if (globalThis.document.activeElement === button) {
						setIsManuallyFocusing(true);
					} else {
						setIsManuallyFocusing(false);
						setMenuIsOpen(false);
					}
				});
			} else {
				setMenuIsOpen(false);
				onBlur && onBlur(e);
			}
		},
		[onBlur],
	);

	const handleCreateOption = useCallback(
		(label: string) => {
			const newLabel: Option = { label, value: label, color: GREY_META.name, __isNew__: true };

			setSelectedLabelForEdit(newLabel);
			setShowColorPickerPopupWhenCreated(true);

			fireTrackAnalyticsDeferred(createAnalyticsEvent({}), 'newLabel created', {
				label,
				fieldId,
			});

			const newLabels = [...(value || []), newLabel];
			onChange?.(newLabels);
		},
		[createAnalyticsEvent, fieldId, onChange, value],
	);

	const menuPosition = isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined;

	const getMenuPosition = () => {
		if (fg('fix_all_dropdown_menu_items_not_visible_in_gic')) {
			return isDropdownMenuFixedAndLayered === true ? 'absolute' : undefined;
		}

		return isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined;
	};

	const isLoading = isLoadingSuggestions;

	const onEditOption = (option: Option) => {
		setSelectedLabelForEdit(option);
		setShowColorPickerPopup(true);
	};

	const resetLabelsColorPickerState = () => {
		setSelectedLabelForEdit(undefined);
		setShowColorPickerPopup(false);
		setShowColorPickerPopupWhenCreated(false);
	};

	const submitLabelsUpdatedColor = (param: ColorOptionData) => {
		if (!selectedLabelForEdit) {
			return;
		}

		setMenuIsOpen(false);

		if (selectedLabelForEdit.__isNew__ && value) {
			const updatedColoredValue =
				value &&
				value.map((val) => {
					const isSelected = val.label === param.name;

					return {
						...val,
						...(isSelected ? { color: param?.color } : {}),
					};
				});
			onChange?.(updatedColoredValue);
		}

		const projectId = project?.projectId;

		let jiraProjectAri = null;
		try {
			jiraProjectAri =
				projectId &&
				cloudId &&
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				) &&
				JiraProjectAri.create({
					siteId: cloudId,
					projectId: projectId.toString(),
				}).toString();
		} catch (error) {
			fireErrorAnalytics({
				meta: {
					id: 'getProjectAriId',
					packageName: 'jiraIssueFieldLabels',
					teamName: 'gryffindor',
				},
				attributes: {
					projectId,
					cloudId,
				},
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				error: error as Error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		const analyticsEvent = createAnalyticsEvent({});

		if (jiraProjectAri) {
			commit({
				variables: {
					input: {
						projectId: jiraProjectAri,
						labelColorUpdate: [
							{
								fieldId,
								label: param.name,
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								color: param.color as JiraOptionColorInput,
							},
						],
					},
				},
				optimisticResponse: {
					jira: {
						bulkUpdateLabelColor: {
							success: true,
							errors: null,
						},
					},
				},
				onCompleted(mutationData) {
					if (mutationData?.jira?.bulkUpdateLabelColor?.success) {
						fetchSuggestions();

						const updatedSuggestions =
							suggestions &&
							suggestions.map((label) => {
								const isSelected = label.label === param.name;

								return {
									...label,
									...(isSelected ? { color: param.color } : {}),
								};
							});
						setSuggestions(updatedSuggestions);

						fireTrackAnalyticsDeferred(analyticsEvent, 'labelColor updated', {
							label: param,
							isNewLabel: selectedLabelForEdit.__isNew__ || false,
						});
					} else {
						const errorMessage =
							mutationData?.jira?.bulkUpdateLabelColor?.errors?.[0]?.message ?? undefined;
						fireErrorAnalytics({
							meta: {
								id: 'labelColorUpdate',
								packageName: 'jiraIssueFieldLabels',
								teamName: 'gryffindor',
							},
							attributes: {
								label: param,
								errorMessage,
								isNewLabel: selectedLabelForEdit.__isNew__ || false,
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				},
				onError(error: Error) {
					fireErrorAnalytics({
						meta: {
							id: 'labelColorUpdate',
							packageName: 'jiraIssueFieldLabels',
							teamName: 'gryffindor',
						},
						attributes: {
							label: param,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		}
	};

	const popupContent = () => {
		const isNewLabel = selectedLabelForEdit?.__isNew__;
		const newLabelColor = selectedLabelForEdit?.color ?? '';
		const oldLabelColor =
			selectedLabelForEdit?.color ??
			memoizeGetAutoGeneratedColorForLabels(selectedLabelForEdit?.label?.toString());

		return (
			<LabelsColorPickerForm
				onCancel={() => {
					resetLabelsColorPickerState();
				}}
				onSubmit={(param: ColorOptionData) => {
					resetLabelsColorPickerState();
					submitLabelsUpdatedColor(param);
				}}
				initialData={{
					name: selectedLabelForEdit?.label?.toString() ?? '',
					color: isNewLabel ? newLabelColor : oldLabelColor,
				}}
				isNewLabel={isNewLabel || false}
			/>
		);
	};

	const styles = useMemo(() => {
		if (
			expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false) ||
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		) {
			return { ...defaultSelectStyles, ...labelsValueStyles };
		}

		return defaultSelectStyles;
	}, []);

	const styleComponents = useMemo(() => {
		if (
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) ||
			expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)
		) {
			return {
				MultiValueContainer: (multiValueProps: MultiValueGenericProps<Option>) => (
					<components.MultiValueContainer {...multiValueProps} xcss={multiValueStyles.root} />
				),
				MultiValueLabel: (multiValueLabelProps: MultiValueGenericProps<Option>) => (
					<components.MultiValueLabel {...multiValueLabelProps} xcss={multiValueLabelStyles.root} />
				),
				MultiValueRemove: (multiValueRemoveProps: MultiValueRemoveProps<Option>) => (
					<components.MultiValueRemove {...multiValueRemoveProps} />
				),
			};
		}
	}, []);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (menuIsOpen === false && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
			setMenuIsOpen(true);
			isFocusingRef.current = false;
		}

		if (e.key === 'Enter') {
			e.preventDefault();
		}

		if (e.key === 'Escape') {
			e.stopPropagation();
		}

		const menu = e.currentTarget.querySelector('[role="dialog"]');
		if (!menu || !['Tab', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;

		const buttons = Array.from(
			menu.querySelectorAll('button:not([disabled]), [role="group"] button:not([disabled])'),
		);

		if (!buttons.length) return;

		e.preventDefault();
		const input = e.currentTarget.querySelector('input');
		const focusableElements = [input, ...buttons].filter(
			(el): el is HTMLElement => el instanceof HTMLElement,
		);

		const currentFocusIndex = focusableElements.findIndex(
			(el) => el === globalThis.document?.activeElement,
		);

		let direction = 1;
		if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
			direction = -1;
		}

		const nextIndex =
			(currentFocusIndex + direction + focusableElements.length) % focusableElements.length;

		focusableElements[nextIndex]?.focus();
		if (nextIndex === 0) {
			isFocusingRef.current = false;
			onQueryChange('');
		} else {
			isFocusingRef.current = true;
		}
	};

	const labelsField = (
		<CreatableSelectWithAnalytics
			isMulti
			autoFocus={autoFocus}
			fieldId={fieldId}
			options={groupedSuggestions}
			value={value}
			isDisabled={
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? isDisabled || ((showColorPickerPopup || showColorPickerPopupWhenCreated) && !isInvalid)
					: isDisabled
			}
			onBlur={onSelectBlur}
			menuPosition={
				fg('fix_all_dropdown_menu_items_not_visible_in_gic') ? getMenuPosition() : menuPosition
			}
			styles={styles}
			onFocus={onSelectFocus}
			noOptionsMessage={getNoOptionsMessage}
			closeMenuOnScroll={onCloseMenuOnScroll}
			loadingMessage={getLoadingMessage}
			placeholder={formatMessage(messages.placeholder)}
			onInputChange={onQueryChange}
			onChange={onChange}
			spacing={spacing}
			isLoading={
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? menuIsOpen && isLoading
					: isLoading
			}
			filterOption={filterOption}
			{...(expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
				? {
						isValidNewOption: (inputValue: string) =>
							inputValue !== '' && isNewLabelText(inputValue),
						onCreateOption: handleCreateOption,
						menuIsOpen: menuIsOpen && !showColorPickerPopupWhenCreated && !showColorPickerPopup,
					}
				: {})}
			formatCreateLabel={getCreateLabel}
			{...(!expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) &&
			expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)
				? {
						formatOptionLabel: (option: Option) =>
							RenderLabel(option, `(${formatMessage(messages.createNewItem)})`),
					}
				: {})}
			components={
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? {
							...styleComponents,
							Option: (optionProps: OptionProps<Option>) => {
								return (
									<LabelFocusableOption
										optionProps={optionProps}
										createNewLabelString={
											formatCreateLabel !== undefined
												? `(${formatCreateLabel})`
												: `(${formatMessage(messages.createNewItem)})`
										}
										onEditOption={onEditOption}
										lastFocusedKey={lastFocusedKey}
										isManuallyFocusing={isManuallyFocusing}
										fieldId={fieldId}
										isProjectAdmin={isProjectAdmin || false}
										isGlobalAdmin={isGlobalAdmin || false}
									/>
								);
							},
						}
					: styleComponents
			}
			validationState={isInvalid === true ? 'error' : null}
			sessionId={sessionId.current}
			aria-label={ariaLabel}
			inputId={inputId}
			isRequired={isRequired}
			openMenuOnFocus={openMenuOnFocus}
			{...(expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
				? { UNSAFE_is_experimental_generic: true, onKeyDown: handleKeyDown }
				: {})}
		/>
	);

	return !isInvalid &&
		(showColorPickerPopup || showColorPickerPopupWhenCreated) &&
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		) ? (
		<Popup
			zIndex={layers.modal()}
			isOpen={showColorPickerPopup || showColorPickerPopupWhenCreated}
			autoFocus={false}
			placement="bottom-start"
			fallbackPlacements={['bottom-end']}
			trigger={(triggerProps: TriggerProps) => (
				<span {...triggerProps} role="menu">
					{labelsField}
				</span>
			)}
			popupComponent={PopupContainer}
			onClose={resetLabelsColorPickerState}
			content={popupContent}
			messageId="issue-field-labels.ui.edit.popup.labels.popup"
			messageType="transactional"
		/>
	) : (
		labelsField
	);
};

const LabelFocusableOption = ({
	optionProps,
	createNewLabelString,
	onEditOption,
	lastFocusedKey,
	isManuallyFocusing,
	fieldId,
	isProjectAdmin,
	isGlobalAdmin,
}: {
	optionProps: OptionProps<Option>;
	createNewLabelString: string;
	onEditOption: (option: Option) => void;
	lastFocusedKey: React.MutableRefObject<{ key: string; index: number; type: string } | null>;
	isManuallyFocusing: boolean;
	fieldId: string;
	isProjectAdmin?: boolean;
	isGlobalAdmin?: boolean;
}) => {
	const [isOptionFocused, setIsOptionFocused] = useState(false);
	const [isOptionHovered, setIsOptionHovered] = useState(false);

	return (
		<components.Option {...optionProps} isFocused={isOptionFocused || isOptionHovered}>
			<RenderColoredLabelOption
				option={optionProps.data}
				createNewLabelString={createNewLabelString}
				onEditOption={onEditOption}
				lastFocusedKey={lastFocusedKey}
				isManuallyFocusing={isManuallyFocusing}
				fieldId={fieldId}
				isProjectAdmin={isProjectAdmin}
				isGlobalAdmin={isGlobalAdmin}
				isOptionFocused={isOptionFocused}
				isOptionHovered={isOptionHovered}
				setIsOptionFocused={setIsOptionFocused}
				setIsOptionHovered={setIsOptionHovered}
			/>
		</components.Option>
	);
};

const RenderLabel = (option: Option, createNewLabelString: string) => {
	// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
	const optionLabel: string = option.label;
	if (typeof optionLabel === 'string' && optionLabel.endsWith(createNewLabelString)) {
		const optionLabelWithoutNewLabel = optionLabel.slice(0, -createNewLabelString.length).trim();

		return (
			<OptionTag
				name={optionLabel}
				color={memoizeGetAutoGeneratedColorForLabels(optionLabelWithoutNewLabel)}
			/>
		);
	}

	return (
		<OptionTag name={optionLabel} color={memoizeGetAutoGeneratedColorForLabels(optionLabel)} />
	);
};

const RenderColoredLabelOption = ({
	option,
	createNewLabelString,
	onEditOption,
	lastFocusedKey,
	isManuallyFocusing,
	fieldId,
	isProjectAdmin,
	isGlobalAdmin,
	isOptionFocused,
	isOptionHovered,
	setIsOptionFocused,
	setIsOptionHovered,
}: {
	option: Option;
	createNewLabelString: string;
	onEditOption: (option: Option) => void;
	lastFocusedKey: React.MutableRefObject<{ key: string; index: number; type: string } | null>;
	isManuallyFocusing: boolean;
	fieldId: string;
	isProjectAdmin?: boolean;
	isGlobalAdmin?: boolean;
	isOptionFocused?: boolean;
	isOptionHovered?: boolean;
	setIsOptionFocused?: (input: boolean) => void;
	setIsOptionHovered?: (input: boolean) => void;
}): JSX.Element => {
	// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
	const optionLabel: string = option.label;
	const { formatMessage } = useIntl();

	const pressableRefsNew = useRef<Record<string, HTMLButtonElement[]>>({});

	useLayoutEffect(() => {
		if (!lastFocusedKey.current) {
			return;
		}
		const { key, index, type } = lastFocusedKey.current;

		if (isManuallyFocusing && pressableRefsNew?.current[key]?.[index] && type === 'button') {
			requestAnimationFrame(() => {
				pressableRefsNew?.current[key]?.[index]?.focus();
			});
		}
	}, [isManuallyFocusing, lastFocusedKey]);

	if (typeof optionLabel === 'string' && optionLabel.endsWith(createNewLabelString)) {
		return (
			<Box
				xcss={optionContentStyles}
				role="group"
				tabIndex={-1}
				onMouseOver={() => {
					setIsOptionFocused?.(false);
					setIsOptionHovered?.(true);
				}}
				onMouseOut={() => setIsOptionHovered?.(false)}
				onFocus={() => {
					setIsOptionHovered?.(false);
					setIsOptionFocused?.(true);
				}}
				onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
					const relatedTarget = e.relatedTarget;
					if (!e.currentTarget.contains(relatedTarget)) {
						setIsOptionFocused?.(false);
					}
				}}
			>
				<Pressable
					xcss={pressableStyles}
					backgroundColor="color.background.neutral.subtle"
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.stopPropagation();
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const input = globalThis.document.querySelector(
								`input#${fieldId}-field`,
							) as HTMLInputElement | null;
							requestAnimationFrame(() => {
								input?.focus();
							});
						}
					}}
					onFocus={() => {
						const focusedKey = lastFocusedKey;
						// eslint-disable-next-line react-hooks/react-compiler
						focusedKey.current = { key: optionLabel, index: 0, type: 'button' };
					}}
					ref={(el) => {
						if (!el) return;

						if (!pressableRefsNew?.current[optionLabel]) {
							pressableRefsNew.current[optionLabel] = [];
						}

						if (!pressableRefsNew.current[optionLabel].includes(el)) {
							pressableRefsNew.current[optionLabel].push(el);
						}
					}}
				>
					<OptionTag name={optionLabel} color={GREY_META.name} />
				</Pressable>
			</Box>
		);
	}

	const iconButtonLabel = formatMessage(messages.editOption, { optionName: optionLabel });

	return (
		<Box
			xcss={optionContentStyles}
			role="group"
			tabIndex={-1}
			onMouseOver={() => {
				setIsOptionFocused?.(false);
				setIsOptionHovered?.(true);
			}}
			onMouseOut={() => setIsOptionHovered?.(false)}
			onFocus={() => {
				setIsOptionHovered?.(false);
				setIsOptionFocused?.(true);
			}}
			onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
				const relatedTarget = e.relatedTarget;
				if (!e.currentTarget.contains(relatedTarget)) {
					setIsOptionFocused?.(false);
				}
			}}
		>
			<Pressable
				xcss={pressableStyles}
				backgroundColor="color.background.neutral.subtle"
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.stopPropagation();
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const input = globalThis.document.querySelector(
							`input#${fieldId}-field`,
						) as HTMLInputElement | null;
						requestAnimationFrame(() => {
							input?.focus();
						});
					}
				}}
				onFocus={() => {
					const focusedKeyRef = lastFocusedKey;
					focusedKeyRef.current = { key: optionLabel, index: 0, type: 'button' };
				}}
				ref={(el) => {
					if (!el) return;

					// Initialize array if not already present
					if (!pressableRefsNew?.current[optionLabel]) {
						pressableRefsNew.current[optionLabel] = [];
					}

					// Avoid duplicate refs
					if (!pressableRefsNew.current[optionLabel].includes(el)) {
						pressableRefsNew.current[optionLabel].push(el);
					}
				}}
			>
				<OptionTag
					name={optionLabel}
					color={option.color ?? memoizeGetAutoGeneratedColorForLabels(optionLabel)}
					isColoredLabel
				/>
			</Pressable>
			{RenderInlineEditButton(
				option,
				lastFocusedKey,
				onEditOption,
				pressableRefsNew,
				isProjectAdmin,
				isGlobalAdmin,
				isOptionFocused || isOptionHovered,
				iconButtonLabel,
			)}
		</Box>
	);
};

const RenderInlineEditButton = (
	option: Option,
	lastFocusedKey: React.MutableRefObject<{ key: string; index: number; type: string } | null>,
	onEditOption: (option: Option) => void,
	pressableRefsNew: React.MutableRefObject<Record<string, HTMLButtonElement[]>>,
	isProjectAdmin?: boolean,
	isGlobalAdmin?: boolean,
	isFocused?: boolean,
	iconButtonLabel?: string,
) => {
	// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
	const optionLabel: string = option.label;

	if ((isProjectAdmin || isGlobalAdmin) && option.label && isFocused) {
		return (
			<IconButton
				ref={(el: HTMLButtonElement | null) => {
					if (!el) return;

					if (!pressableRefsNew?.current[optionLabel].includes(el)) {
						pressableRefsNew?.current[optionLabel].push(el);
					}
				}}
				onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.stopPropagation();
					}
				}}
				onFocus={() => {
					const focusedKeyRef = lastFocusedKey;
					focusedKeyRef.current = { key: optionLabel, index: 1, type: 'button' };
				}}
				testId={`labels-field-edit.inline-edit-button-${option.label}`}
				isDisabled={option.isDisabled}
				appearance="subtle"
				icon={EditIcon}
				spacing="compact"
				tabIndex={0}
				onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
					event.stopPropagation();
					onEditOption?.(option);
				}}
				label={iconButtonLabel}
			/>
		);
	}
};

type ExtendedPopupComponentProps = PopupComponentProps & {
	onClick?: (e: React.MouseEvent) => void;
};

const PopupContainer = forwardRef<HTMLDivElement, ExtendedPopupComponentProps>((props, ref) => (
	<div {...props} ref={ref} />
));

export default LabelsEdit;

const optionContentStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	justifyContent: 'space-between',
});
const pressableStyles = xcss({ height: '24px' });
